import type { TemplateId } from '@doriavboard/shared';

function Note({ x, y, color, size = 14 }: { x: number; y: number; color: string; size?: number }) {
  return <rect x={x} y={y} width={size} height={size} rx="1.5" fill={color} />;
}

function Column({ x, header, body = '#f1f5f9' }: { x: number; header: string; body?: string }) {
  return (
    <g>
      <rect x={x} y={14} width={34} height={8} rx="2" fill={header} />
      <rect x={x} y={25} width={34} height={58} rx="2" fill={body} />
    </g>
  );
}

/** Small illustration of what a template creates, for the "new board" gallery. */
export function TemplatePreview({ id }: { id: TemplateId }) {
  let content: React.ReactNode = null;
  switch (id) {
    case 'blank':
      content = (
        <path d="M80 40v20M70 50h20" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" />
      );
      break;
    case 'brainstorm': {
      const ideas = ['#fff176', '#ffcc80', '#d1c4e9', '#b3e5fc', '#c5e1a5', '#b2dfdb'];
      content = (
        <g>
          {ideas.map((color, i) => {
            const a = ((-90 + i * 60) * Math.PI) / 180;
            const x = 80 + Math.cos(a) * 46;
            const y = 50 + Math.sin(a) * 32;
            return (
              <g key={color}>
                <line x1="80" y1="50" x2={x} y2={y} stroke="#94a3b8" strokeWidth="1.2" />
                <Note x={x - 7} y={y - 7} color={color} />
              </g>
            );
          })}
          <Note x={69} y={39} color="#f8bbd0" size={22} />
        </g>
      );
      break;
    }
    case 'retro':
      content = (
        <g>
          <Column x={21} header="#34a853" body="#e6f4ea" />
          <Column x={63} header="#f29900" body="#fef7e0" />
          <Column x={105} header="#4285f4" body="#e8f0fe" />
          <Note x={25} y={30} color="#fff176" size={11} />
          <Note x={67} y={30} color="#fff176" size={11} />
          <Note x={109} y={30} color="#fff176" size={11} />
        </g>
      );
      break;
    case 'kanban':
      content = (
        <g>
          <Column x={21} header="#5f6368" />
          <Column x={63} header="#1a73e8" />
          <Column x={105} header="#188038" />
          <Note x={25} y={30} color="#fff176" size={11} />
          <Note x={39} y={30} color="#fff176" size={11} />
          <Note x={25} y={45} color="#fff176" size={11} />
          <Note x={67} y={30} color="#ffcc80" size={11} />
        </g>
      );
      break;
    case 'swot':
      content = (
        <g>
          <rect x="34" y="12" width="44" height="36" rx="3" fill="#e6f4ea" />
          <rect x="82" y="12" width="44" height="36" rx="3" fill="#fce8e6" />
          <rect x="34" y="52" width="44" height="36" rx="3" fill="#e8f0fe" />
          <rect x="82" y="52" width="44" height="36" rx="3" fill="#fef7e0" />
          <rect x="38" y="16" width="16" height="3" rx="1" fill="#188038" />
          <rect x="86" y="16" width="16" height="3" rx="1" fill="#c5221f" />
          <rect x="38" y="56" width="16" height="3" rx="1" fill="#1967d2" />
          <rect x="86" y="56" width="16" height="3" rx="1" fill="#e37400" />
        </g>
      );
      break;
    case 'weekly':
      content = (
        <g>
          {Array.from({ length: 7 }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static decoration
            <g key={i}>
              <rect
                x={14 + i * 19}
                y={16}
                width={16}
                height={6}
                rx="1.5"
                fill={i >= 5 ? '#9334e6' : '#1a73e8'}
              />
              <rect x={14 + i * 19} y={25} width={16} height={58} rx="1.5" fill="#f1f5f9" />
            </g>
          ))}
        </g>
      );
      break;
  }
  return (
    <svg viewBox="0 0 160 100" className="size-full" aria-hidden="true">
      <defs>
        <pattern id={`tp-dots-${id}`} width="8" height="8" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.8" fill="#cbd5e1" />
        </pattern>
      </defs>
      <rect width="160" height="100" fill="#fff" />
      <rect width="160" height="100" fill={`url(#tp-dots-${id})`} />
      {content}
    </svg>
  );
}
